import { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  Select,
  SimpleGrid,
  GridItem,
  NumberInput,
  NumberInputField,
} from "@chakra-ui/react";
import axiosInstance from "../../../helpers/axiosInstance";
import Translate from "../../../components/LanguageTranslation/Translate";

export default function AddRentalCar({
  isOpen,
  onClose,
  addTripItem,
  defaultSubcategories = [],
  editItem = null,
  onEditSuccess = null,
}) {
  // you can inspect defaultSubcategories.includes("rental_cars") if needed
  const [company, setCompany] = useState("");
  const [pickupLocation, setPickupLocation] = useState("");
  const [dropoffLocation, setDropoffLocation] = useState("");
  const [pickupDate, setPickupDate] = useState("");
  const [dropoffDate, setDropoffDate] = useState("");
  const [price, setPrice] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      if (editItem) {
        // Populate form with existing data for editing
        setCompany(editItem.company || "");
        setPickupLocation(editItem.pickupLocation || "");
        setDropoffLocation(editItem.dropoffLocation || "");
        setPickupDate(editItem.pickupDate ? new Date(editItem.pickupDate).toISOString().split('T')[0] : "");
        setDropoffDate(editItem.dropoffDate ? new Date(editItem.dropoffDate).toISOString().split('T')[0] : "");
        setPrice(editItem.price?.amount?.toString() || "");
        setCurrency(editItem.price?.currency || "USD");
      } else {
        // Reset form for new rental car
        setCompany("");
        setPickupLocation("");
        setDropoffLocation("");
        setPickupDate("");
        setDropoffDate("");
        setPrice("");
        setCurrency("USD");
      }
      setErrors({});
    }
  }, [isOpen, editItem]);

  const validate = () => {
    const e = {};
    if (!company.trim()) e.company = "Company name required";
    if (!pickupLocation.trim()) e.pickupLocation = "Pickup location required";
    if (!dropoffLocation.trim())
      e.dropoffLocation = "Drop-off location required";
    if (!pickupDate) e.pickupDate = "Pickup date required";
    if (!dropoffDate) e.dropoffDate = "Drop-off date required";
    if (!price || isNaN(price) || price <= 0) e.price = "Valid price required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const payload = {
      company,
      pickup_location: pickupLocation,
      dropoff_location: dropoffLocation,
      pickup_date: new Date(pickupDate).toISOString(),
      dropoff_date: new Date(dropoffDate).toISOString(),
      price: parseFloat(price),
      currency,
    };

    // Add item_id to payload if editing
    if (editItem && editItem._id) {
      payload.item_id = editItem._id;
    }

    try {
      const response = await axiosInstance.post(
        "/bucket-list/add-manual-rental-car",
        payload
      );
      if (response.data.success) {
        const rentalCarData = {
          company,
          pickupLocation: pickupLocation,
          dropoffLocation: dropoffLocation,
          pickupDate: new Date(pickupDate).toISOString(),
          dropoffDate: new Date(dropoffDate).toISOString(),
          price: { amount: parseFloat(price), currency },
        };

        if (editItem && editItem._id) {
          // Update existing item
          const updatedItem = {
            ...editItem,
            data: rentalCarData,
          };
          if (onEditSuccess) {
            onEditSuccess(updatedItem);
          }
        } else {
          // Add new item
          const newItem = {
            _id: response.data.data._id,
            data: rentalCarData,
            category: "transportation",
            preference: "rental_car",
            email: localStorage.getItem("email"),
          };

          // Only wrap in data for 'explore', otherwise add as-is
          if (newItem.category === "explore") {
            addTripItem({ ...newItem, data: { ...newItem } });
          } else {
            addTripItem(newItem);
          }
        }
      }
    } catch (err) {
      console.error(err);
    }

    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent maxH="80vh" overflowY="auto">
        <ModalHeader>
          <Translate text={editItem ? "Edit Rental Car" : "Add Rental Cars"} />
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <SimpleGrid columns={2} spacing={4}>
            <GridItem colSpan={2}>
              <FormControl isInvalid={errors.company}>
                <FormLabel>
                  {" "}
                  <Translate text="Company" />
                </FormLabel>
                <Input
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                />
                <FormErrorMessage>
                  {" "}
                  <Translate text={errors.company} />
                </FormErrorMessage>
              </FormControl>
            </GridItem>

            <GridItem colSpan={1}>
              <FormControl isInvalid={errors.pickupLocation}>
                <FormLabel>
                  {" "}
                  <Translate text="Pickup Location " />
                </FormLabel>
                <Input
                  value={pickupLocation}
                  onChange={(e) => setPickupLocation(e.target.value)}
                />
                <FormErrorMessage>
                  {" "}
                  <Translate text={errors.pickupLocation} />
                </FormErrorMessage>
              </FormControl>
            </GridItem>

            <GridItem colSpan={1}>
              <FormControl isInvalid={errors.dropoffLocation}>
                <FormLabel>
                  {" "}
                  <Translate text="Drop-off Location   " />
                </FormLabel>
                <Input
                  value={dropoffLocation}
                  onChange={(e) => setDropoffLocation(e.target.value)}
                />
                <FormErrorMessage>
                  {" "}
                  <Translate text={errors.dropoffLocation} />
                </FormErrorMessage>
              </FormControl>
            </GridItem>

            <GridItem colSpan={1}>
              <FormControl isInvalid={errors.pickupDate}>
                <FormLabel>
                  {" "}
                  <Translate text="Pickup Date  " />
                </FormLabel>
                <Input
                  type="date"
                  value={pickupDate}
                  onChange={(e) => setPickupDate(e.target.value)}
                />
                <FormErrorMessage>
                  {" "}
                  <Translate text={errors.pickupDate} />
                </FormErrorMessage>
              </FormControl>
            </GridItem>
            <GridItem colSpan={1}>
              <FormControl isInvalid={errors.dropoffDate}>
                <FormLabel>
                  {" "}
                  <Translate text="Drop-off Date " />
                </FormLabel>
                <Input
                  type="date"
                  value={dropoffDate}
                  onChange={(e) => setDropoffDate(e.target.value)}
                />
                <FormErrorMessage>
                  {" "}
                  <Translate text={errors.dropoffDate} />
                </FormErrorMessage>
              </FormControl>
            </GridItem>

            <GridItem colSpan={1}>
              <FormControl isInvalid={errors.price}>
                <FormLabel>
                  {" "}
                  <Translate text="Price" />
                </FormLabel>
                <NumberInput value={price} onChange={(_, v) => setPrice(v)}>
                  <NumberInputField />
                </NumberInput>
                <FormErrorMessage>
                  {" "}
                  <Translate text={errors.price} />
                </FormErrorMessage>
              </FormControl>
            </GridItem>
            <GridItem colSpan={1}>
              <FormControl>
                <FormLabel>
                  {" "}
                  <Translate text="Currency" />
                </FormLabel>
                <Select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                >
                  <option value="USD">USD $</option>
                  <option value="EUR">EUR €</option>
                  <option value="GBP">GBP £</option>
                  {/* add more as needed */}
                </Select>
              </FormControl>
            </GridItem>
          </SimpleGrid>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="customGreen" mr={3} onClick={handleSave}>
            <Translate text="Save" />
          </Button>
          <Button onClick={onClose}>
            {" "}
            <Translate text="Cancel" />
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
